import React, { useEffect, useRef, useState } from 'react'

import { Button, Drawer, Form, Input, Spin, Table } from 'antd'
import Heading from '../../../components/Heading/Heading'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import {
  addFortyBay,
  addTwentyBay,
  deleteFortyBay,
  deleteTwentyBay,
  saveFortyBayConfig,
  saveTwentyBayConfig,
  updateFortyBayCheckbox,
} from '../helpers'
import { getBayColumns, mapEditableColumns } from '../tableColumns'
import { editableComponents } from './EditableTable'
import { VALIDATION_RULES } from '../validation'

/**
 * Vessel Drawer Component
 * Displays a drawer for creating, updating, or cloning vessel information with real-time validation
 */
const VesselDrawer = ({
  drawerTitle,
  drawerType,
  drawerVisible,
  handleDrawerClose,
  handleDrawerSubmit,
  initialConfig,
  isLoading,
  isSaving,
  selectedRecord,
  setSelectedRecord,
}) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = useState('')
  const [formValuesChanged, setFormValuesChanged] = useState(0)

  // Internal bay configuration state (moved from Admin)
  const [bayConfigurations, setBayConfigurations] = useState([])
  const [bayCount, setBayCount] = useState(0)
  const [twentiesConfigurations, setTwentiesConfigurations] = useState([])
  const [twentiesCount, setTwentiesCount] = useState(0)
  const [accommodationBay, setAccommodationBay] = useState('')
  const [engineBay, setEngineBay] = useState('')
  const [lowestDeckBay, setLowestDeckBay] = useState('')
  const [mergeBays, setMergeBays] = useState(false)
  const [deckMaxRange, setDeckMaxRange] = useState('')
  const [deckMinRange, setDeckMinRange] = useState('')
  const [holdMaxRange, setHoldMaxRange] = useState('')
  const [holdMinRange, setHoldMinRange] = useState('')
  const [rowMaxRange, setRowMaxRange] = useState('')
  const [rowMinRange, setRowMinRange] = useState('')

  // Store original values for change detection (UPDATE mode only)
  const originalValuesRef = useRef(null)
  const [hasStoredOriginalValues, setHasStoredOriginalValues] = useState(false)

  const isCreateMode = drawerType === 'create'
  const isCloneMode = drawerType === 'clone'
  const isUpdateMode = drawerType === 'update'
  const isEditableMode = isCreateMode || isCloneMode

  // Seed internal bay state from initialConfig when drawer opens
  useEffect(() => {
    if (drawerVisible && initialConfig) {
      setBayConfigurations(initialConfig.bayConfigurations || [])
      setBayCount((initialConfig.bayConfigurations || []).length)
      setTwentiesConfigurations(initialConfig.twentiesConfigurations || [])
      setTwentiesCount((initialConfig.twentiesConfigurations || []).length)
      setAccommodationBay(initialConfig.accommodationBay || '')
      setEngineBay(initialConfig.engineBay || '')
      setLowestDeckBay(initialConfig.lowestDeckBay || '')
      setMergeBays(initialConfig.mergeBays || false)
      setDeckMaxRange(initialConfig.deckMaxRange || '')
      setDeckMinRange(initialConfig.deckMinRange || '')
      setHoldMaxRange(initialConfig.holdMaxRange || '')
      setHoldMinRange(initialConfig.holdMinRange || '')
      setRowMaxRange(initialConfig.rowMaxRange || '')
      setRowMinRange(initialConfig.rowMinRange || '')

      // Store original values directly from initialConfig for UPDATE mode
      // Must be done here so originals match the seeded state exactly
      if (isUpdateMode && !hasStoredOriginalValues) {
        originalValuesRef.current = {
          accommodationBay: initialConfig.accommodationBay || '',
          bayConfigurations: JSON.parse(
            JSON.stringify(initialConfig.bayConfigurations || [])
          ),
          deckMaxRange: initialConfig.deckMaxRange || '',
          deckMinRange: initialConfig.deckMinRange || '',
          engineBay: initialConfig.engineBay || '',
          holdMaxRange: initialConfig.holdMaxRange || '',
          holdMinRange: initialConfig.holdMinRange || '',
          mergeBays: initialConfig.mergeBays || false,
          rowMaxRange: initialConfig.rowMaxRange || '',
          rowMinRange: initialConfig.rowMinRange || '',
          twentiesConfigurations: JSON.parse(
            JSON.stringify(initialConfig.twentiesConfigurations || [])
          ),
        }
        setHasStoredOriginalValues(true)
      }
    }
  }, [drawerVisible, initialConfig, isUpdateMode, hasStoredOriginalValues])

  // Set form values when selectedRecord changes
  useEffect(() => {
    // Only set form values if drawer is visible AND selectedRecord exists
    if (selectedRecord && drawerVisible) {
      // Set the form values
      form.setFieldsValue({
        abbreviation: selectedRecord.vesselAbbreviation,
        accommodationBay,
        deckMaxRange,
        deckMinRange,
        engineBay,
        holdMaxRange,
        holdMinRange,
        imoNumber: String(selectedRecord.imoNumber ?? ''),
        rowMaxRange,
        rowMinRange,
        vesselName: selectedRecord.vesselName,
      })
    }
  }, [
    selectedRecord,
    accommodationBay,
    deckMaxRange,
    deckMinRange,
    engineBay,
    holdMaxRange,
    holdMinRange,
    form,
    drawerVisible,
    rowMaxRange,
    rowMinRange,
  ])

  // Clear error message, original values, and bay state when drawer closes
  useEffect(() => {
    if (!drawerVisible) {
      setErrorMessage('')
      originalValuesRef.current = null
      setHasStoredOriginalValues(false)
      // Reset internal bay state
      setBayConfigurations([])
      setBayCount(0)
      setTwentiesConfigurations([])
      setTwentiesCount(0)
      setAccommodationBay('')
      setEngineBay('')
      setLowestDeckBay('')
      setMergeBays(false)
      setDeckMaxRange('')
      setDeckMinRange('')
      setHoldMaxRange('')
      setHoldMinRange('')
      setRowMaxRange('')
      setRowMinRange('')
    }
  }, [drawerVisible, form])

  /**
   * Check if any values have changed from the original (UPDATE mode only)
   * @returns {boolean} - True if changes detected, false otherwise
   */
  const hasUnsavedChanges = () => {
    // In CREATE or CLONE mode, always allow saving
    if (!isUpdateMode) {
      return true
    }

    // Original values not yet stored — treat as no changes (keep button disabled)
    if (!originalValuesRef.current) {
      return false
    }

    const original = originalValuesRef.current

    // Check simple field changes
    if (
      original.accommodationBay !== accommodationBay ||
      original.deckMaxRange !== deckMaxRange ||
      original.deckMinRange !== deckMinRange ||
      original.engineBay !== engineBay ||
      original.holdMaxRange !== holdMaxRange ||
      original.holdMinRange !== holdMinRange ||
      original.mergeBays !== mergeBays ||
      original.rowMaxRange !== rowMaxRange ||
      original.rowMinRange !== rowMinRange
    ) {
      return true
    }

    // Check bay configurations changes (forties)
    if (
      JSON.stringify(original.bayConfigurations) !==
      JSON.stringify(bayConfigurations)
    ) {
      return true
    }

    // Check twenties configurations changes
    if (
      JSON.stringify(original.twentiesConfigurations) !==
      JSON.stringify(twentiesConfigurations)
    ) {
      return true
    }

    // No changes detected
    return false
  }

  /**
   * Check if required fields are filled for CREATE/CLONE mode
   * @returns {boolean} - True if all required fields are filled
   */
  const areRequiredFieldsFilled = () => {
    // Get current form values to check real-time input
    const formValues = form.getFieldsValue()

    // Check vessel basic info from form values (real-time)
    const vesselName = formValues.vesselName || selectedRecord?.vesselName
    const abbreviation =
      formValues.abbreviation || selectedRecord?.vesselAbbreviation
    const imoNumber = String(
      formValues.imoNumber ?? selectedRecord?.imoNumber ?? ''
    )

    if (!vesselName?.trim() || !abbreviation?.trim() || !imoNumber.trim()) {
      return false
    }

    // Check if at least one bay configuration exists
    if (bayConfigurations.length === 0 && twentiesConfigurations.length === 0) {
      return false
    }

    // Check bay configurations have required fields (all modes)
    const allFortyBaysValid = bayConfigurations.every(
      config => config.bay?.trim() && config.hatchCovers?.trim()
    )
    const allTwentyBaysValid = twentiesConfigurations.every(
      config => config.bay?.trim() && config.hatchCovers?.trim()
    )

    if (!allFortyBaysValid || !allTwentyBaysValid) {
      return false
    }

    // Accommodation and Engine bay are required in all modes
    if (!accommodationBay?.trim() || !engineBay?.trim()) {
      return false
    }

    return true
  }

  /**
   * Check if submit button should be disabled
   * @returns {boolean} - True if button should be disabled
   */
  const isSubmitDisabled = () => {
    // Force re-evaluation when form values change
    // eslint-disable-next-line no-unused-expressions
    formValuesChanged

    // Disable if currently loading or saving
    if (isLoading || isSaving) {
      return true
    }

    // Check for any form validation errors (applies to all modes)
    const hasFormErrors = form.getFieldsError().some(f => f.errors.length > 0)
    if (hasFormErrors) {
      return true
    }

    // All range fields are required across all modes
    const requiredRangesFilled =
      deckMaxRange?.trim() &&
      deckMinRange?.trim() &&
      holdMaxRange?.trim() &&
      holdMinRange?.trim() &&
      rowMaxRange?.trim() &&
      rowMinRange?.trim()
    if (!requiredRangesFilled) {
      return true
    }

    // For UPDATE mode, disable if no changes made
    if (isUpdateMode && !hasUnsavedChanges()) {
      return true
    }

    // For CREATE/CLONE mode, disable if required fields are missing
    if ((isCreateMode || isCloneMode) && !areRequiredFieldsFilled()) {
      return true
    }

    // For UPDATE mode, also enforce required field checks (bays, accommodation, engine)
    if (isUpdateMode && !areRequiredFieldsFilled()) {
      return true
    }

    return false
  }

  const handleSubmit = () => {
    setErrorMessage('')

    // Validate that no bay value appears in both Forty Foot Bays and Twenty Foot Bays
    const fortyBayValues = bayConfigurations
      .map(config => config.bay?.trim())
      .filter(Boolean)
    const twentyBayValues = twentiesConfigurations
      .map(config => config.bay?.trim())
      .filter(Boolean)

    const duplicateBay = fortyBayValues.find(bay =>
      twentyBayValues.includes(bay)
    )
    if (duplicateBay) {
      setErrorMessage(
        t('admin.vesselDrawer.validation.duplicateBayValues', {
          bay: duplicateBay,
        })
      )

      return
    }

    try {
      const formValues = form.getFieldsValue()

      // Compose the full submission payload from all drawer state and form values
      const payload = {
        accommodationBay,
        bayConfigurations,
        deckMaxRange,
        deckMinRange,
        engineBay,
        holdMaxRange,
        holdMinRange,
        imoNumber: String(formValues.imoNumber ?? '').trim(),
        lowestDeckBay,
        mergeBays,
        rowMaxRange,
        rowMinRange,
        twentiesConfigurations,
        vesselAbbreviation: String(formValues.abbreviation ?? '').trim(),
        vesselName: String(formValues.vesselName ?? '').trim(),
        ...(isUpdateMode && { vesselId: selectedRecord?.vesselId }),
      }

      // Pass the composed payload and onError callback to the parent handler
      // onError will display backend error messages inside the drawer
      handleDrawerSubmit(payload, message => setErrorMessage(message))
      // If successful (no error thrown), parent saga will handle closing via callback
    } catch (error) {
      // Display error message below buttons
      setErrorMessage(
        error.message || t('admin.vesselDrawer.errors.submitFailed')
      )
    }
  }

  const handleVesselFieldChange = (field, value) => {
    setSelectedRecord({
      ...selectedRecord,
      [field]: value,
    })
    // Trigger re-render to update button state
    setFormValuesChanged(prev => prev + 1)
  }

  // Handle form field changes to trigger button state updates
  const handleFormValuesChange = () => {
    setFormValuesChanged(prev => prev + 1)
  }

  // Bay configuration handlers (moved from Admin)
  const handleAddBay = () => {
    addFortyBay({
      bayConfigurations,
      bayCount,
      setBayConfigurations,
      setBayCount,
    })
  }

  const handleDeleteBay = key => {
    deleteFortyBay({ bayConfigurations, key, setBayConfigurations })
  }

  const handleBayConfigSave = row => {
    saveFortyBayConfig({ bayConfigurations, row, setBayConfigurations })
  }

  const handleFortyBayCheckboxChange = (key, value) => {
    updateFortyBayCheckbox({
      bayConfigurations,
      key,
      setBayConfigurations,
      value,
    })
  }

  const handleAddTwenties = () => {
    addTwentyBay({
      setTwentiesConfigurations,
      setTwentiesCount,
      twentiesConfigurations,
      twentiesCount,
    })
  }

  const handleDeleteTwenties = key => {
    deleteTwentyBay({ key, setTwentiesConfigurations, twentiesConfigurations })
  }

  const handleTwentiesConfigSave = row => {
    saveTwentyBayConfig({
      row,
      setTwentiesConfigurations,
      twentiesConfigurations,
    })
  }

  // Compute table column configs internally
  const bayColumns = getBayColumns(
    handleDeleteBay,
    t,
    handleFortyBayCheckboxChange,
    true
  )
  const bayColumnsWithEdit = mapEditableColumns(bayColumns, handleBayConfigSave)
  const twentiesColumns = getBayColumns(handleDeleteTwenties, t, null, false)
  const twentiesColumnsWithEdit = mapEditableColumns(
    twentiesColumns,
    handleTwentiesConfigSave
  )

  // Validation rules for Vessel Name
  const vesselNameRules = [
    {
      message: t('admin.vesselDrawer.validation.vesselNameRequired'),
      whitespace: true,
    },
    { message: t('admin.vesselDrawer.validation.vesselNameMinLength'), min: 2 },
    {
      max: VALIDATION_RULES.vesselName.max,
      message: t('admin.vesselDrawer.validation.vesselNameMaxLength'),
    },
  ]

  // Validation rules for Abbreviation
  const abbreviationRules = [
    {
      validator: (_, value) => {
        const valueStr = String(value || '').trim()

        if (!valueStr) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.vesselAbbreviationRequired')
            )
          )
        }
        if (valueStr.length < 3) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.vesselAbbreviationMinLength')
            )
          )
        }
        if (valueStr.length > 4) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.vesselAbbreviationMaxLength')
            )
          )
        }
        if (!/^[A-Z]+$/.test(valueStr)) {
          return Promise.reject(
            new Error(
              t(
                'admin.vesselDrawer.validation.vesselAbbreviationUppercaseAlpha'
              )
            )
          )
        }

        return Promise.resolve()
      },
    },
  ]

  // Validation rules for IMO Number
  const imoNumberRules = [
    {
      validator: (_, value) => {
        // Convert to string for validation
        const valueStr = String(value || '').trim()

        if (!valueStr) {
          return Promise.reject(
            new Error(t('admin.vesselDrawer.validation.imoNumberRequired'))
          )
        }
        if (!/^\d+$/.test(valueStr)) {
          return Promise.reject(
            new Error(t('admin.vesselDrawer.validation.imoNumberDigitsOnly'))
          )
        }
        if (valueStr.length !== 7) {
          return Promise.reject(
            new Error(t('admin.vesselDrawer.validation.imoNumberExactLength'))
          )
        }
        const imoNum = parseInt(valueStr, 10)
        if (imoNum < 1000000 || imoNum > 9999999) {
          return Promise.reject(
            new Error(t('admin.vesselDrawer.validation.imoNumberRange'))
          )
        }

        return Promise.resolve()
      },
    },
  ]

  // Validation rules for bay fields - single validator to prevent double errors
  const getBayFieldRules = fieldName => [
    {
      validator: (_, value) => {
        const str = String(value ?? '').trim()

        // Required in all modes
        if (!str) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.bayFieldRequired', { fieldName })
            )
          )
        }

        if (!/^\d+$/.test(str)) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.bayFieldDigitsOnly', {
                fieldName,
              })
            )
          )
        }
        const num = parseInt(str, 10)
        if (num < 1 || num > 99) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.bayFieldRange', { fieldName })
            )
          )
        }

        return Promise.resolve()
      },
    },
  ]

  // Validation rules for tier/row range fields (required, non-negative integers, max 999)
  const getRangeFieldRules = fieldName => [
    {
      validator: (_, value) => {
        const str = String(value ?? '').trim()
        if (!str) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.rangeFieldRequired', {
                fieldName,
              })
            )
          )
        }
        if (!/^\d+$/.test(str)) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.rangeFieldDigitsOnly', {
                fieldName,
              })
            )
          )
        }
        const num = parseInt(str, 10)
        if (num < 0) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.rangeFieldNonNegative', {
                fieldName,
              })
            )
          )
        }
        if (num > VALIDATION_RULES.tierRowRange.max) {
          return Promise.reject(
            new Error(
              t('admin.vesselDrawer.validation.rangeFieldMax', {
                fieldName,
                max: VALIDATION_RULES.tierRowRange.max,
              })
            )
          )
        }

        return Promise.resolve()
      },
    },
  ]

  return (
    <Drawer
      destroyOnClose
      onClose={handleDrawerClose}
      open={drawerVisible}
      title={drawerTitle}
      width={700}
    >
      {/* Only render content when drawer is visible and record is selected */}
      {drawerVisible && selectedRecord ? (
        <Spin
          className="admin-drawer-spin"
          spinning={isLoading}
          tip={t('admin.vesselDrawer.loading')}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleFormValuesChange}
          >
            {/* Vessel information - editable in create and clone mode */}
            <div className="vessel-info-readonly">
              <Form.Item
                name="vesselName"
                rules={vesselNameRules}
                validateTrigger={['onChange', 'onBlur']}
                label={
                  <span>
                    {t('admin.vesselDrawer.vesselName')}
                    <span className="admin-drawer__required-asterisk">*</span>
                  </span>
                }
              >
                <Input
                  disabled={!isEditableMode}
                  onChange={e =>
                    handleVesselFieldChange('vesselName', e.target.value)
                  }
                />
              </Form.Item>
              <Form.Item
                name="abbreviation"
                rules={abbreviationRules}
                validateTrigger="onChange"
                label={
                  <span>
                    {t('admin.vesselDrawer.vesselAbbreviation')}
                    <span className="admin-drawer__required-asterisk">*</span>
                  </span>
                }
              >
                <Input
                  disabled={!isEditableMode}
                  onChange={e =>
                    handleVesselFieldChange(
                      'vesselAbbreviation',
                      e.target.value
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="imoNumber"
                rules={imoNumberRules}
                validateTrigger={['onChange', 'onBlur']}
                label={
                  <span>
                    {t('admin.vesselDrawer.imoNumber')}
                    <span className="admin-drawer__required-asterisk">*</span>
                  </span>
                }
              >
                <Input
                  disabled={!isEditableMode}
                  onChange={e =>
                    handleVesselFieldChange('imoNumber', e.target.value)
                  }
                />
              </Form.Item>
            </div>

            {/* Bay Configuration Section */}
            <div className="bay-configuration-section">
              <h3>{t('admin.vesselDrawer.bayConfiguration')}</h3>

              {/* Forties Section */}
              <div className="bay-section">
                <Heading level={4}>
                  {t('admin.vesselDrawer.fortyFootBays')}
                </Heading>
                <Table
                  bordered
                  columns={bayColumnsWithEdit}
                  components={editableComponents}
                  dataSource={bayConfigurations}
                  pagination={false}
                  rowClassName={() => 'editable-row'}
                  size="small"
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 10,
                  }}
                >
                  <Button
                    className="add-bay-button"
                    onClick={handleAddBay}
                    type="button"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Twenties Section */}
              <div className="bay-section">
                <Heading level={4}>
                  {t('admin.vesselDrawer.twentyFootBays')}
                </Heading>
                <Table
                  bordered
                  columns={twentiesColumnsWithEdit}
                  components={editableComponents}
                  dataSource={twentiesConfigurations}
                  pagination={false}
                  rowClassName={() => 'editable-row'}
                  size="small"
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 10,
                  }}
                >
                  <Button
                    className="add-bay-button"
                    onClick={handleAddTwenties}
                    type="button"
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Compact Sections - Horizontal Layout */}
              <div className="bay-compact-sections">
                <Form.Item
                  label={t('admin.vesselDrawer.accommodationAfterBay')}
                  name="accommodationBay"
                  validateTrigger="onBlur"
                  rules={getBayFieldRules(
                    t('admin.vesselDrawer.accommodationAfterBay')
                  )}
                >
                  <Input
                    onChange={e => setAccommodationBay(e.target.value)}
                    value={accommodationBay}
                    placeholder={t(
                      'admin.vesselDrawer.placeholders.accommodationAfterBay'
                    )}
                  />
                </Form.Item>

                <Form.Item
                  label={t('admin.vesselDrawer.engineAfterBay')}
                  name="engineBay"
                  validateTrigger="onBlur"
                  rules={getBayFieldRules(
                    t('admin.vesselDrawer.engineAfterBay')
                  )}
                >
                  <Input
                    onChange={e => setEngineBay(e.target.value)}
                    value={engineBay}
                  />
                </Form.Item>

                <Form.Item label={t('admin.vesselDrawer.mergeBays')}>
                  <input
                    checked={mergeBays}
                    onChange={e => setMergeBays(e.target.checked)}
                    type="checkbox"
                  />
                </Form.Item>
              </div>

              {/* Tier & Row Labels Range Section */}
              <div className="tier-row-labels-section">
                <div className="range-group">
                  <Heading level={4}>
                    {t('admin.vesselDrawer.deckTierLabelsRange')}
                  </Heading>
                  <div className="range-group__inputs">
                    <Form.Item
                      name="deckMaxRange"
                      validateTrigger={['onChange', 'onBlur']}
                      label={
                        <span>
                          {t('admin.vesselDrawer.deckMaxRange')}
                          <span className="admin-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={getRangeFieldRules(
                        t('admin.vesselDrawer.deckMaxRange')
                      )}
                    >
                      <Input
                        onChange={e => setDeckMaxRange(e.target.value)}
                        value={deckMaxRange}
                        placeholder={t(
                          'admin.vesselDrawer.placeholders.deckMaxRange'
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      name="deckMinRange"
                      validateTrigger={['onChange', 'onBlur']}
                      label={
                        <span>
                          {t('admin.vesselDrawer.deckMinRange')}
                          <span className="admin-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={getRangeFieldRules(
                        t('admin.vesselDrawer.deckMinRange')
                      )}
                    >
                      <Input
                        onChange={e => setDeckMinRange(e.target.value)}
                        value={deckMinRange}
                        placeholder={t(
                          'admin.vesselDrawer.placeholders.deckMinRange'
                        )}
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className="range-group">
                  <Heading level={4}>
                    {t('admin.vesselDrawer.holdTierLabelsRange')}
                  </Heading>
                  <div className="range-group__inputs">
                    <Form.Item
                      name="holdMaxRange"
                      validateTrigger={['onChange', 'onBlur']}
                      label={
                        <span>
                          {t('admin.vesselDrawer.holdMaxRange')}
                          <span className="admin-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={getRangeFieldRules(
                        t('admin.vesselDrawer.holdMaxRange')
                      )}
                    >
                      <Input
                        onChange={e => setHoldMaxRange(e.target.value)}
                        value={holdMaxRange}
                        placeholder={t(
                          'admin.vesselDrawer.placeholders.holdMaxRange'
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      name="holdMinRange"
                      validateTrigger={['onChange', 'onBlur']}
                      label={
                        <span>
                          {t('admin.vesselDrawer.holdMinRange')}
                          <span className="admin-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={getRangeFieldRules(
                        t('admin.vesselDrawer.holdMinRange')
                      )}
                    >
                      <Input
                        onChange={e => setHoldMinRange(e.target.value)}
                        value={holdMinRange}
                        placeholder={t(
                          'admin.vesselDrawer.placeholders.holdMinRange'
                        )}
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className="range-group">
                  <Heading level={4}>
                    {t('admin.vesselDrawer.rowLabelsRange')}
                  </Heading>
                  <div className="range-group__inputs">
                    <Form.Item
                      name="rowMaxRange"
                      validateTrigger={['onChange', 'onBlur']}
                      label={
                        <span>
                          {t('admin.vesselDrawer.rowMaxRange')}
                          <span className="admin-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={getRangeFieldRules(
                        t('admin.vesselDrawer.rowMaxRange')
                      )}
                    >
                      <Input
                        onChange={e => setRowMaxRange(e.target.value)}
                        value={rowMaxRange}
                        placeholder={t(
                          'admin.vesselDrawer.placeholders.rowMaxRange'
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      name="rowMinRange"
                      validateTrigger={['onChange', 'onBlur']}
                      label={
                        <span>
                          {t('admin.vesselDrawer.rowMinRange')}
                          <span className="admin-drawer__required-asterisk">
                            *
                          </span>
                        </span>
                      }
                      rules={getRangeFieldRules(
                        t('admin.vesselDrawer.rowMinRange')
                      )}
                    >
                      <Input
                        onChange={e => setRowMinRange(e.target.value)}
                        value={rowMinRange}
                        placeholder={t(
                          'admin.vesselDrawer.placeholders.rowMinRange'
                        )}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>

            <Form.Item>
              <div className="admin-drawer__actions">
                <Button
                  className="admin-drawer__cancel"
                  onClick={handleDrawerClose}
                  size="middle"
                  type="default"
                >
                  {t('admin.vesselDrawer.cancel')}
                </Button>
                <Button
                  disabled={isSubmitDisabled()}
                  htmlType="submit"
                  size="middle"
                  type="primary"
                >
                  {isCreateMode
                    ? t('admin.vesselDrawer.create')
                    : isCloneMode
                      ? t('admin.vesselDrawer.clone')
                      : t('admin.vesselDrawer.update')}
                </Button>
              </div>
            </Form.Item>

            {errorMessage && (
              <div className="admin-drawer-error-message">
                <span>{errorMessage}</span>
                <button
                  className="admin-drawer-error-close"
                  onClick={() => setErrorMessage('')}
                  type="button"
                >
                  ×
                </button>
              </div>
            )}
          </Form>
        </Spin>
      ) : null}
    </Drawer>
  )
}

VesselDrawer.propTypes = {
  drawerTitle: PropTypes.string.isRequired,
  drawerType: PropTypes.string,
  drawerVisible: PropTypes.bool.isRequired,
  handleDrawerClose: PropTypes.func.isRequired,
  handleDrawerSubmit: PropTypes.func.isRequired,
  initialConfig: PropTypes.shape({
    accommodationBay: PropTypes.string,
    bayConfigurations: PropTypes.array,
    deckMaxRange: PropTypes.string,
    deckMinRange: PropTypes.string,
    engineBay: PropTypes.string,
    holdMaxRange: PropTypes.string,
    holdMinRange: PropTypes.string,
    lowestDeckBay: PropTypes.string,
    mergeBays: PropTypes.bool,
    rowMaxRange: PropTypes.string,
    rowMinRange: PropTypes.string,
    twentiesConfigurations: PropTypes.array,
  }),
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool.isRequired,
  selectedRecord: PropTypes.object,
  setSelectedRecord: PropTypes.func.isRequired,
}

export default VesselDrawer
